import { invariantResponse } from '@epic-web/invariant'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

// TypeScript declaration for Instagram embed
declare global {
	interface Window {
		instgrm?: {
			Embeds: {
				process: () => void
			}
		}
	}
}
import { ConsensusCard } from '#app/components/consensus-card.tsx'
import { WantToGoButton } from '#app/components/want-to-go-button.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import {
	calculateConsensus,
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl, getRestaurantImgSrc } from '#app/utils/misc.tsx'
import {
	getRelatedRestaurants,
	getVibeTagIds,
} from '#app/utils/related-restaurants.server.ts'
import { type Route } from './+types/london.restaurants.$slug.tsx'

// Static images for restaurants that don't have uploaded images yet
const STATIC_IMAGES: Record<string, string> = {
	'bleecker-burger': '/images/bleeker.png',
}

// Generate Google Maps URL from Place ID (preferred) or fallback to mapUrl
// Uses the Google Maps URLs API format which works on both desktop and mobile
function getLocationMapUrl(
	location:
		| { googlePlaceId?: string | null; mapUrl?: string | null }
		| null
		| undefined,
	fallbackMapUrl?: string | null,
	restaurantName?: string,
	areaName?: string,
): string {
	// Prefer Google Place ID with proper API format for cross-platform support
	if (location?.googlePlaceId) {
		const query = encodeURIComponent(`${restaurantName ?? ''} London`.trim())
		return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${location.googlePlaceId}`
	}
	// Fallback to location mapUrl
	if (location?.mapUrl) {
		return location.mapUrl
	}
	// Fallback to restaurant mapUrl
	if (fallbackMapUrl) {
		return fallbackMapUrl
	}
	// Generate search URL as last resort
	return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${restaurantName ?? ''} ${areaName ?? ''} London`.trim())}`
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) {
		return [
			{ title: 'Restaurant not found | Good to Order' },
			{ name: 'robots', content: 'noindex' },
		]
	}

	const { restaurant, heroImage, canonical, breadcrumbArea, orderPicks } = data
	const areaName = breadcrumbArea?.name ?? 'London'
	const safeFirstDish = orderPicks?.[0]?.title
	const year = new Date().getFullYear()
	const price = restaurant.priceRange ? ` ${restaurant.priceRange}` : ''

	const title = safeFirstDish
		? `${restaurant.name} Review (${year}) | Start with ${safeFirstDish}`
		: `${restaurant.name} Review (${year}) | What to Order`

	const description = safeFirstDish
		? `${restaurant.cuisine}${price} in ${areaName}. Start with the ${safeFirstDish}. ${restaurant.summaryOneLiner ?? 'Quick take, best bets, and review signal.'}`
		: `${restaurant.cuisine}${price} in ${areaName}. ${restaurant.summaryOneLiner ?? 'Quick take, what to order first, and review signal.'}`

	return [
		{ title },
		{ name: 'description', content: description.slice(0, 155) },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		...(heroImage ? [{ property: 'og:image', content: heroImage }] : []),
		{ rel: 'canonical', href: canonical },
	]
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const { slug } = params
	const neighborhood = (params as { neighborhood?: string }).neighborhood
	invariantResponse(slug, 'Restaurant not found', { status: 404 })

	// Allow preview mode for logged-in users
	const requestUrl = new URL(request.url)
	const isPreview = requestUrl.searchParams.get('preview') === 'true'
	const userId = await getUserId(request)
	const canPreview = isPreview && userId

	const restaurant = await prisma.restaurant.findFirst({
		where: {
			slug: slug.toLowerCase(),
			// Only require published status if not in preview mode
			...(canPreview ? {} : { status: 'published' }),
			...(neighborhood
				? {
						tags: {
							some: {
								tag: {
									slug: neighborhood.toLowerCase(),
									type: 'area',
								},
							},
						},
					}
				: {}),
		},
		include: {
			picks: { orderBy: { sortOrder: 'asc' } },
			notes: { orderBy: { sortOrder: 'asc' } },
			locations: { orderBy: { sortOrder: 'asc' } },
			tags: { include: { tag: true } },
			primaryArea: true,
			images: true,
			signals: { orderBy: { createdAt: 'desc' } },
		},
	})

	invariantResponse(restaurant, 'Restaurant not found', { status: 404 })

	// Check if this restaurant is in user's want-to-go list
	let isWantToGo = false
	if (userId) {
		try {
			isWantToGo = !!(await prisma.wantToGo.findUnique({
				where: { userId_restaurantId: { userId, restaurantId: restaurant.id } },
			}))
		} catch {
			// Table may not exist yet in production
		}
	}

	const url = new URL(request.url)
	const canonical = `${getDomainUrl(request)}${url.pathname}`

	// Group picks by type
	const orderPicks = restaurant.picks.filter((p) =>
		['safe_first', 'hungry', 'side', 'wildcard'].includes(p.type),
	)
	const skipPicks = restaurant.picks.filter((p) => p.type === 'skip')

	// Group notes by type
	const specialsNote = restaurant.notes.find((n) => n.type === 'specials_note')
	const strategyNote = restaurant.notes.find((n) => n.type === 'strategy')
	const goodToKnowNote = restaurant.notes.find((n) => n.type === 'good_to_know')

	// Get area tags for display
	const areaTags = restaurant.tags
		.filter((rt) => rt.tag.type === 'area')
		.map((rt) => rt.tag)

	// Get primary image or first image
	const primaryImage =
		restaurant.images.find((img) => img.isPrimary) ?? restaurant.images[0]

	// Determine hero image URL - check S3 first, then static fallback
	const heroImage = primaryImage
		? getRestaurantImgSrc(primaryImage.objectKey)
		: (STATIC_IMAGES[restaurant.slug] ?? null)

	// Nearby: other restaurants in the same primary area
	const nearby = restaurant.primaryAreaId
		? await prisma.restaurant.findMany({
				where: {
					id: { not: restaurant.id },
					status: 'published',
					primaryAreaId: restaurant.primaryAreaId,
				},
				orderBy: { consensusScore: 'desc' },
				take: 3,
				select: {
					id: true,
					name: true,
					slug: true,
					cuisine: true,
					priceRange: true,
					consensusScore: true,
				},
			})
		: []

	// Dish cluster: find other restaurants with the same key dish slug
	const clusterDishSlug =
		orderPicks.find((p) => p.dishSlug)?.dishSlug ??
		skipPicks.find((p) => p.dishSlug)?.dishSlug ??
		null

	const dishCluster = clusterDishSlug
		? await prisma.restaurant.findMany({
				where: {
					id: { not: restaurant.id },
					status: 'published',
					picks: {
						some: { dishSlug: clusterDishSlug },
					},
				},
				orderBy: [{ consensusScore: 'desc' }],
				take: 4,
				select: {
					id: true,
					name: true,
					slug: true,
					cuisine: true,
					priceRange: true,
					consensusScore: true,
					primaryArea: { select: { slug: true, name: true } },
				},
			})
		: []

	// Get related restaurants for internal linking (SEO)
	const vibeTagIds = getVibeTagIds(restaurant.tags)
	const relatedRestaurants = await getRelatedRestaurants(restaurant.id, {
		cuisine: restaurant.cuisine,
		priceRange: restaurant.priceRange,
		tagIds: vibeTagIds,
		primaryAreaId: restaurant.primaryAreaId,
		limit: 4,
	})

	const consensus = calculateConsensus(restaurant)
	const scoreContext = getScoreContext(consensus.total)
	const consensusBreakdown = {
		...consensus.breakdown,
		boost: consensus.breakdown.boost ?? 0,
		hiddenGem: consensus.breakdown.hiddenGem ?? 0,
		weightedAvg: consensus.breakdown.weightedAvg ?? 0,
	}
	const eliteSignals = restaurant.signals.filter((s) => s.type === 'ELITE')
	const editorialSignals = restaurant.signals.filter(
		(s) => s.type === 'EDITORIAL',
	)

	const locationsByNeighborhood = restaurant.locations.reduce<
		Record<string, typeof restaurant.locations>
	>((acc, loc) => {
		const key = loc.neighborhoodId ?? 'other'
		if (!acc[key]) acc[key] = []
		acc[key]!.push(loc)
		return acc
	}, {})

	// Get Instagram data from DB
	const instagramHandle = restaurant.instagramHandle ?? null
	const instagramPostId = restaurant.instagramPostId ?? null

	// Get all images for carousel (sorted, primary first)
	const restaurantImages = restaurant.images
		.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
		.map((img) => ({
			id: img.id,
			url: getRestaurantImgSrc(img.objectKey),
			altText: img.altText,
			isPrimary: img.isPrimary,
		}))

	return {
		restaurant: {
			id: restaurant.id,
			name: restaurant.name,
			slug: restaurant.slug,
			cuisine: restaurant.cuisine,
			priceRange: restaurant.priceRange,
			summaryOneLiner: restaurant.summaryOneLiner,
			visited: restaurant.visited,
			confidence: restaurant.confidence,
			menuCheckedAt: restaurant.menuCheckedAt,
			mapUrl: restaurant.mapUrl,
			socialMentions: restaurant.socialMentions,
			googleRating: restaurant.googleRating ?? 0,
			consensusScore: consensus.total,
			eliteSignalCount: eliteSignals.length,
			editorialSignalCount: editorialSignals.length,
			totalReviewCount: restaurant.totalReviewCount ?? 0,
			manualBoost: restaurant.manualBoost,
			isHiddenGem: restaurant.isHiddenGem,
			isCultClassic: restaurant.isCultClassic,
			isDateSpot: restaurant.isDateSpot,
			isGroupFriendly: restaurant.isGroupFriendly,
			isLoudAndParty: restaurant.isLoudAndParty,
			isCounterDining: restaurant.isCounterDining,
			doesGreatSandwiches: restaurant.doesGreatSandwiches,
			primaryArea: restaurant.primaryArea
				? {
						id: restaurant.primaryArea.id,
						name: restaurant.primaryArea.name,
						slug: restaurant.primaryArea.slug,
					}
				: null,
			instagramHandle,
			instagramPostId,
			isWantToGo,
		},
		nearby,
		dishCluster,
		cuisineSiblings: relatedRestaurants.cuisineSiblings,
		vibeSiblings: relatedRestaurants.vibeSiblings,
		priceSiblings: relatedRestaurants.priceSiblings,
		scoreContext,
		orderPicks,
		skipPicks,
		specialsNote,
		strategyNote,
		goodToKnowNote,
		locations: restaurant.locations,
		locationsByNeighborhood,
		areaTags,
		heroImage,
		restaurantImages,
		canonical,
		breadcrumbArea: restaurant.primaryArea ?? areaTags[0] ?? null,
		consensusConfidence: consensus.confidence,
		consensusRisk: consensus.riskNote,
		consensusBreakdown,
	}
}

export default function RestaurantPage({ loaderData }: Route.ComponentProps) {
	const {
		restaurant,
		nearby,
		dishCluster,
		cuisineSiblings,
		vibeSiblings,
		scoreContext,
		orderPicks,
		skipPicks,
		specialsNote,
		strategyNote,
		goodToKnowNote,
		locations,
		areaTags,
		heroImage,
		restaurantImages,
		consensusConfidence,
		consensusRisk,
		consensusBreakdown,
	} = loaderData

	// Carousel state for multiple images
	const [currentImageIndex, setCurrentImageIndex] = useState(0)
	const hasMultipleImages = restaurantImages.length > 1

	// Load Instagram embed script when we have a post to embed
	useEffect(() => {
		if (restaurant.instagramPostId && typeof window !== 'undefined') {
			// Check if script already exists
			if (!document.getElementById('instagram-embed-script')) {
				const script = document.createElement('script')
				script.id = 'instagram-embed-script'
				script.src = '//www.instagram.com/embed.js'
				script.async = true
				document.body.appendChild(script)
			} else if (window.instgrm) {
				// If script exists, just process embeds
				window.instgrm.Embeds.process()
			}
		}
	}, [restaurant.instagramPostId])

	const googleRating = Number(restaurant.googleRating ?? 0)
	const totalReviews = Number(restaurant.totalReviewCount ?? 0)
	const hasConsistencyBonus = googleRating >= 4.8 && totalReviews >= 500
	const displayPrice =
		restaurant.priceRange &&
		!restaurant.priceRange.toLowerCase().includes('unknown')
			? restaurant.priceRange
			: '£'
	const ldRestaurant = {
		'@context': 'https://schema.org',
		'@type': 'Restaurant',
		name: restaurant.name,
		url: loaderData.canonical,
		image: heroImage || undefined,
		priceRange: restaurant.priceRange,
		servesCuisine: restaurant.cuisine,
		...(googleRating
			? {
					aggregateRating: {
						'@type': 'AggregateRating',
						ratingValue: googleRating,
						reviewCount: totalReviews || undefined,
					},
				}
			: {}),
		address: locations[0]?.address
			? {
					'@type': 'PostalAddress',
					streetAddress: locations[0].address,
					addressLocality:
						loaderData.breadcrumbArea?.name ?? locations[0].name ?? 'London',
					addressRegion: 'London',
					addressCountry: 'GB',
				}
			: undefined,
		geo:
			locations[0]?.latitude != null && locations[0]?.longitude != null
				? {
						'@type': 'GeoCoordinates',
						latitude: locations[0].latitude,
						longitude: locations[0].longitude,
					}
				: undefined,
	}

	const canonicalOrigin = (() => {
		try {
			return new URL(loaderData.canonical).origin
		} catch {
			return ''
		}
	})()

	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: `${canonicalOrigin}/`,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: `${canonicalOrigin}/london`,
			},
			...(loaderData.breadcrumbArea
				? [
						{
							'@type': 'ListItem',
							position: 3,
							name: loaderData.breadcrumbArea.name,
							item: `${canonicalOrigin}/london/best/restaurants-${loaderData.breadcrumbArea.slug}`,
						},
					]
				: []),
			{
				'@type': 'ListItem',
				position: loaderData.breadcrumbArea ? 4 : 3,
				name: restaurant.name,
				item: loaderData.canonical,
			},
		],
	}

	// Schema for related restaurants (SEO internal linking signals)
	const allRelated = [
		...cuisineSiblings.map((r) => ({ ...r, relationType: 'cuisine' })),
		...vibeSiblings.map((r) => ({ ...r, relationType: 'vibe' })),
	]

	const ldRelatedRestaurants =
		allRelated.length > 0
			? {
					'@context': 'https://schema.org',
					'@type': 'ItemList',
					name: `Restaurants similar to ${restaurant.name}`,
					description: `Related ${restaurant.cuisine} restaurants and similar vibes in London`,
					numberOfItems: allRelated.length,
					itemListElement: allRelated.map((r, index) => ({
						'@type': 'ListItem',
						position: index + 1,
						item: {
							'@type': 'Restaurant',
							name: r.name,
							url: `${canonicalOrigin}/london/restaurants/${r.slug}`,
							servesCuisine: r.cuisine,
							priceRange: r.priceRange,
							...(r.primaryArea
								? {
										address: {
											'@type': 'PostalAddress',
											addressLocality: r.primaryArea.name,
											addressRegion: 'London',
										},
									}
								: {}),
						},
					})),
				}
			: null

	const feedbackPagePath = (() => {
		try {
			return new URL(loaderData.canonical).pathname
		} catch {
			return `/london/restaurants/${restaurant.slug}`
		}
	})()

	const pageIssueFeedbackHref = `/support?type=page_issue&restaurantId=${encodeURIComponent(restaurant.id)}&pagePath=${encodeURIComponent(feedbackPagePath)}&pageUrl=${encodeURIComponent(loaderData.canonical)}`
	const featureRequestFeedbackHref = `/support?type=feature_request&restaurantId=${encodeURIComponent(restaurant.id)}&pagePath=${encodeURIComponent(feedbackPagePath)}&pageUrl=${encodeURIComponent(loaderData.canonical)}`

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldRestaurant) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>
			{ldRelatedRestaurants && (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(ldRelatedRestaurants),
					}}
				/>
			)}
			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-8 lg:py-10">
					{/* Breadcrumb */}
					<nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<Link to="/london" className="hover:text-[#422ad5]">
							London
						</Link>
						{(restaurant.primaryArea ?? areaTags[0]) && (
							<>
								<span>/</span>
								<Link
									to={`/london/best/restaurants-${(restaurant.primaryArea ?? areaTags[0])!.slug}`}
									className="hover:text-[#422ad5]"
								>
									{(restaurant.primaryArea ?? areaTags[0])!.name}
								</Link>
							</>
						)}
						<span>/</span>
						<span className="text-[#f8f8ff]">{restaurant.name}</span>
					</nav>

					{/* Two-column hero layout */}
					<div className="grid items-start gap-6 lg:grid-cols-2">
						{/* Left: Own images carousel > Instagram embed > fallback card */}
						{restaurantImages.length > 0 ? (
							/* Image carousel with our own photos */
							<div className="card overflow-hidden rounded-xl border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_30px_rgba(0,224,255,0.08)]">
								<div className="relative aspect-[4/3] bg-[#0a0a12]">
									<img
										src={restaurantImages[currentImageIndex]?.url}
										alt={
											restaurantImages[currentImageIndex]?.altText ??
											`${restaurant.name} photo ${currentImageIndex + 1}`
										}
										className="absolute inset-0 h-full w-full object-cover"
									/>
									{/* Carousel controls */}
									{hasMultipleImages && (
										<>
											<button
												onClick={() =>
													setCurrentImageIndex((prev) =>
														prev === 0 ? restaurantImages.length - 1 : prev - 1,
													)
												}
												className="absolute top-1/2 left-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
												aria-label="Previous image"
											>
												<svg
													className="h-5 w-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M15 19l-7-7 7-7"
													/>
												</svg>
											</button>
											<button
												onClick={() =>
													setCurrentImageIndex((prev) =>
														prev === restaurantImages.length - 1 ? 0 : prev + 1,
													)
												}
												className="absolute top-1/2 right-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
												aria-label="Next image"
											>
												<svg
													className="h-5 w-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</button>
											{/* Dots indicator */}
											<div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
												{restaurantImages.map((_, idx) => (
													<button
														key={idx}
														onClick={() => setCurrentImageIndex(idx)}
														className={`h-2 w-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/40'}`}
														aria-label={`Go to image ${idx + 1}`}
													/>
												))}
											</div>
										</>
									)}
								</div>
								{/* Image count indicator */}
								<div className="px-4 py-3 text-sm text-[#b0b0e0]">
									<span>
										{currentImageIndex + 1} / {restaurantImages.length} photos
									</span>
								</div>
							</div>
						) : restaurant.instagramPostId ? (
							/* Instagram embed when no own photos */
							<div className="mx-auto">
								<div className="max-h-[500px] max-w-[380px] overflow-hidden rounded-xl">
									<blockquote
										className="instagram-media"
										data-instgrm-permalink={`https://www.instagram.com/p/${restaurant.instagramPostId}/`}
										data-instgrm-version="14"
										style={{
											background: '#FFF',
											border: 0,
											borderRadius: '3px',
											boxShadow: 'none',
											margin: 0,
											maxWidth: '100%',
											padding: 0,
											width: '100%',
										}}
									>
										<a
											href={`https://www.instagram.com/p/${restaurant.instagramPostId}/`}
											target="_blank"
											rel="noopener noreferrer"
											className="block p-4 text-center text-[#3897f0]"
										>
											View this post on Instagram
										</a>
									</blockquote>
								</div>
							</div>
						) : (
							/* Fallback Instagram-style card */
							<div className="card flex flex-col overflow-hidden rounded-xl border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_30px_rgba(0,224,255,0.08)]">
								{/* Instagram header */}
								<div className="flex items-center gap-3 border-b border-[#1a1a2e] px-4 py-3">
									<div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#422ad5] to-[#00e0ff] text-xs font-bold">
										{restaurant.name.charAt(0)}
									</div>
									<div className="flex-1">
										<span className="text-sm font-semibold text-[#f8f8ff]">
											{restaurant.instagramHandle ??
												restaurant.slug.replace(/-/g, '_')}
										</span>
									</div>
									{restaurant.instagramHandle && (
										<a
											href={`https://instagram.com/${restaurant.instagramHandle}`}
											target="_blank"
											rel="noopener noreferrer"
											className="rounded border border-[#00e0ff] px-3 py-1 text-xs font-semibold text-[#00e0ff] transition-colors hover:bg-[#00e0ff10]"
										>
											Follow
										</a>
									)}
								</div>
								{/* Image or gradient placeholder */}
								<div className="relative aspect-square max-h-[400px]">
									{heroImage ? (
										<img
											src={heroImage}
											alt={`${restaurant.name} signature dish`}
											className="absolute inset-0 h-full w-full object-cover"
										/>
									) : (
										<div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#422ad5] via-[#1a1a2e] to-[#00e0ff]">
											<span className="text-[80px] font-black tracking-tight text-white/90 select-none">
												{restaurant.name.charAt(0).toUpperCase()}
											</span>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Right: Restaurant info card */}
						<div className="card flex flex-col rounded-xl border border-[#1a1a2e] bg-[#0d0d15] p-6 shadow-[0_0_30px_rgba(66,42,213,0.12)]">
							{/* Name + Score */}
							<div className="mb-4 flex items-start justify-between gap-4">
								<h1 className="text-3xl font-black tracking-tight text-[#f8f8ff] sm:text-4xl lg:text-5xl">
									{restaurant.name}
								</h1>
								<div className="flex items-center gap-3">
									<WantToGoButton
										restaurantId={restaurant.id}
										initialIsWantToGo={restaurant.isWantToGo}
										size="md"
									/>
									<div className="text-right">
										<div
											className="ml-auto flex h-14 w-14 items-center justify-center rounded-full border-2 text-xl font-bold shadow-[0_0_15px_rgba(0,224,255,0.3)]"
											style={{
												borderColor: scoreContext.color,
												color: scoreContext.color,
												boxShadow: `0 0 15px ${scoreContext.color}4d`,
											}}
											title={getScoreExplainer(restaurant.consensusScore)}
										>
											{restaurant.consensusScore}
										</div>
										<p
											className="mt-2 text-[11px] font-semibold tracking-[0.18em] uppercase"
											style={{ color: scoreContext.color }}
										>
											Signal score
										</p>
										<p className="max-w-[180px] text-[11px] text-[#7070a0]">
											{getScoreSupportText(restaurant.consensusScore)}
										</p>
									</div>
								</div>
							</div>

							{/* Summary */}
							{restaurant.summaryOneLiner && (
								<p className="mb-5 text-base leading-relaxed text-[#b0b0e0] italic">
									{restaurant.summaryOneLiner}
								</p>
							)}

							{/* Metadata grid */}
							<div className="mb-5 grid grid-cols-2 gap-4">
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-xs tracking-wider text-[#7070a0] uppercase">
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m18-4.5v5.25c0 1.035-.84 1.875-1.875 1.875H4.875A1.875 1.875 0 013 17.25V12m18 0c0-1.035-.84-1.875-1.875-1.875H4.875C3.84 10.125 3 10.965 3 12m18 0v.375"
											/>
										</svg>
										Cuisine
									</div>
									<p className="text-sm font-medium text-[#f8f8ff]">
										{restaurant.cuisine}
									</p>
								</div>
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-xs tracking-wider text-[#7070a0] uppercase">
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										Price
									</div>
									<p className="text-sm font-medium text-[#f8f8ff]">
										{displayPrice}
									</p>
								</div>
								<div className="space-y-1">
									<div className="flex items-center gap-2 text-xs tracking-wider text-[#7070a0] uppercase">
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
											/>
										</svg>
										Neighborhood
									</div>
									<p className="text-sm font-medium text-[#f8f8ff]">
										{(restaurant.primaryArea ?? areaTags[0])?.name ?? 'London'}
									</p>
								</div>
								<a
									href={getLocationMapUrl(
										locations[0],
										restaurant.mapUrl,
										restaurant.name,
										restaurant.primaryArea?.name,
									)}
									target="_blank"
									rel="noopener noreferrer"
									className="group space-y-1"
								>
									<div className="flex items-center gap-2 text-xs tracking-wider text-[#7070a0] uppercase transition-colors group-hover:text-[#00e0ff]">
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
											/>
										</svg>
										Location
									</div>
									<p className="flex items-center gap-1 text-sm font-medium text-[#00e0ff] transition-colors group-hover:text-[#00e0ff]/80">
										{locations[0]?.address || 'View Map'}
										<svg
											className="h-3 w-3"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
									</p>
								</a>
							</div>

							{/* Badges */}
							<div className="mb-5 flex flex-wrap gap-2">
								{restaurant.isHiddenGem && (
									<span className="badge border border-[#00e0ff] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#00e0ff] uppercase">
										Hidden Gem
									</span>
								)}
								{restaurant.isCultClassic && (
									<span className="badge border border-[#ff9f1c] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#ff9f1c] uppercase">
										Cult Classic
									</span>
								)}
								{hasConsistencyBonus && (
									<span className="badge border border-[#22c55e] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#22c55e] uppercase">
										Locals' Choice 4.8★
									</span>
								)}
								{restaurant.isDateSpot && (
									<span className="badge border border-[#ff6b9d] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#ff6b9d] uppercase">
										Date Spot
									</span>
								)}
								{restaurant.isGroupFriendly && (
									<span className="badge border border-[#a78bfa] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#a78bfa] uppercase">
										Group Friendly
									</span>
								)}
								{restaurant.isCounterDining && (
									<span className="badge border border-[#fbbf24] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#fbbf24] uppercase">
										Counter Dining
									</span>
								)}
								{restaurant.isLoudAndParty && (
									<span className="badge border border-[#f97316] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#f97316] uppercase">
										Loud & Party
									</span>
								)}
								{restaurant.doesGreatSandwiches && (
									<span className="badge border border-[#84cc16] px-3 py-1.5 text-[11px] tracking-[0.12em] text-[#84cc16] uppercase">
										Great Sandwiches
									</span>
								)}
							</div>

							{/* Ordering Strategy */}
							{strategyNote && (
								<div className="mt-auto border-t border-[#1a1a2e] pt-4">
									<div className="rounded-lg border-l-4 border-[#422ad5] bg-[#151525] p-4">
										<p className="mb-1 text-[10px] font-semibold tracking-[0.15em] text-[#422ad5] uppercase">
											Ordering Strategy
										</p>
										<p className="text-sm leading-relaxed text-[#b0b0e0]">
											{strategyNote.body}
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</section>

			<div className="container mx-auto px-6 py-10">
				<div className="grid gap-8 lg:grid-cols-3">
					{/* Main Content */}
					<div className="space-y-8 lg:col-span-2">
						{/* What to Order */}
						<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_25px_rgba(66,42,213,0.15)]">
							<div className="card-body space-y-4 text-[#f8f8ff]">
								<h2 className="card-title text-3xl font-black tracking-tight text-[#422ad5]">
									What to order
								</h2>
								<div className="space-y-4">
									{orderPicks.map((pick) => (
										<div
											key={pick.id}
											className="flex gap-4 rounded-xl bg-[#151525] p-4"
										>
											<div className="flex-shrink-0">
												<span
													className={`badge border px-3 py-2 text-[11px] tracking-[0.1em] uppercase ${
														pick.type === 'safe_first'
															? 'border-[#422ad5] text-[#422ad5]'
															: pick.type === 'hungry'
																? 'border-[#00e0ff] text-[#00e0ff]'
																: pick.type === 'wildcard'
																	? 'border-[#ff9f1c] text-[#ff9f1c]'
																	: 'border-[#ff2e63] text-[#ff2e63]'
													}`}
												>
													{pick.type === 'safe_first'
														? 'Safe first'
														: pick.type === 'hungry'
															? 'Hungry'
															: pick.type === 'wildcard'
																? 'Wildcard'
																: 'Side'}
												</span>
											</div>
											<div className="flex-1 space-y-1">
												<h3 className="text-lg font-semibold text-[#f8f8ff]">
													{pick.title}
												</h3>
												{pick.tip && (
													<p className="text-sm leading-relaxed text-[#b0b0e0]">
														{pick.tip}
													</p>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						</section>

						{/* Specials */}
						{specialsNote && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-3xl font-black tracking-tight text-[#422ad5]">
										Specials
									</h2>
									<p className="text-base leading-relaxed text-[#b0b0e0]">
										{specialsNote.body}
									</p>
								</div>
							</section>
						)}

						{/* What to Skip */}
						{skipPicks.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-4 text-[#f8f8ff]">
									<h2 className="card-title text-3xl font-black tracking-tight text-[#ff2e63]">
										What to skip
									</h2>
									<div className="space-y-3">
										{skipPicks.map((pick) => (
											<div
												key={pick.id}
												className="flex gap-4 rounded-lg bg-[#151525] p-3"
											>
												<span className="badge border border-[#ff2e63] px-3 py-2 text-[11px] tracking-[0.1em] text-[#ff2e63] uppercase">
													Skip
												</span>
												<div>
													<span className="text-base font-medium text-[#f8f8ff]">
														{pick.title}
													</span>
													{pick.tip && (
														<span className="ml-2 text-sm text-[#b0b0e0]">
															— {pick.tip}
														</span>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							</section>
						)}
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Nearby in area */}
						{nearby.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										More in {restaurant.primaryArea?.name ?? 'this area'}
									</h2>
									<ul className="space-y-2 text-sm">
										{nearby.map((r) => (
											<li
												key={r.id}
												className="flex items-center justify-between gap-3"
											>
												<Link
													to={`/london/restaurants/${r.slug}`}
													className="hover:text-[#00e0ff]"
												>
													{r.name}
												</Link>
												{(() => {
													const ctx = getScoreContext(r.consensusScore ?? 0)
													return (
														<span className="flex items-center gap-2 text-[#7070a0]">
															{r.cuisine} · {r.priceRange}
															<span
																className="badge badge-xs font-semibold"
																style={{
																	borderColor: ctx.color,
																	color: ctx.color,
																	backgroundColor: `${ctx.color}1a`,
																}}
																title={ctx.label}
															>
																{r.consensusScore ?? 0}
															</span>
														</span>
													)
												})()}
											</li>
										))}
									</ul>
									{restaurant.primaryArea && (
										<Link
											to={`/london/best/restaurants-${restaurant.primaryArea.slug}`}
											className="text-sm text-[#00e0ff] hover:underline"
										>
											See all restaurants in {restaurant.primaryArea.name} →
										</Link>
									)}
								</div>
							</section>
						)}

						<ConsensusCard
							score={restaurant.consensusScore}
							breakdown={consensusBreakdown}
							confidence={consensusConfidence}
							eliteCount={restaurant.eliteSignalCount}
							editorialCount={restaurant.editorialSignalCount}
							googleRating={googleRating}
							totalReviews={totalReviews}
							socialMentions={restaurant.socialMentions}
							riskNote={consensusRisk}
						/>

						{/* Good to Know */}
						{goodToKnowNote && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										Good to know
									</h2>
									<p className="text-sm leading-relaxed text-[#b0b0e0]">
										{goodToKnowNote.body}
									</p>
								</div>
							</section>
						)}

						{/* Locations */}
						{locations.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										Locations
									</h2>
									<ul className="space-y-3">
										{locations.map((loc) => (
											<li key={loc.id}>
												<a
													href={getLocationMapUrl(
														loc,
														null,
														restaurant.name,
														loc.name,
													)}
													target="_blank"
													rel="noopener noreferrer"
													className="flex items-center gap-2 text-sm text-[#b0b0e0] hover:text-[#00e0ff]"
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														fill="none"
														viewBox="0 0 24 24"
														strokeWidth={1.5}
														stroke="currentColor"
														className="size-4"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
														/>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
														/>
													</svg>
													{loc.name}
													{loc.address && (
														<span className="text-[#7070a0]">
															{loc.address}
														</span>
													)}
												</a>
											</li>
										))}
									</ul>
								</div>
							</section>
						)}

						{/* Trust Signals */}
						<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
							<div className="card-body space-y-3 text-[#f8f8ff]">
								<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
									Trust signals
								</h2>
								<ul className="space-y-2 text-sm">
									<li className="flex items-center gap-2">
										<span
											className={`badge badge-sm px-3 py-2 text-[11px] tracking-[0.1em] uppercase ${restaurant.visited ? 'border border-[#00e0ff] text-[#00e0ff]' : 'border border-[#b0b0e0] text-[#b0b0e0]'}`}
										>
											{restaurant.visited ? '✓' : '○'}
										</span>
										{restaurant.visited ? 'Visited' : 'Not yet visited'}
									</li>
									<li className="flex items-center gap-2">
										<span className="badge badge-sm border border-[#00e0ff] px-3 py-2 text-[11px] tracking-[0.1em] text-[#00e0ff] uppercase">
											{restaurant.confidence}
										</span>
										Confidence level
									</li>
									{restaurant.menuCheckedAt && (
										<li className="flex items-center gap-2">
											<span className="badge badge-sm border border-[#b0b0e0] px-3 py-2 text-[11px] tracking-[0.1em] text-[#b0b0e0] uppercase">
												{restaurant.menuCheckedAt}
											</span>
											Menu last checked
										</li>
									)}
								</ul>
							</div>
						</section>

						{/* Dish cluster */}
						{dishCluster.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										More great{' '}
										{orderPicks.find((p) => p.dishLabel)?.dishLabel ?? 'dishes'}{' '}
										in London
									</h2>
									<ul className="space-y-2 text-sm">
										{dishCluster.map((r) => (
											<li
												key={r.id}
												className="flex items-center justify-between gap-3"
											>
												<Link
													to={`/london/restaurants/${r.slug}`}
													className="hover:text-[#00e0ff]"
												>
													{r.name}
												</Link>
												{(() => {
													const ctx = getScoreContext(r.consensusScore ?? 0)
													return (
														<span className="flex items-center gap-2 text-[#7070a0]">
															{r.cuisine} · {r.priceRange}
															<span
																className="badge badge-xs font-semibold"
																style={{
																	borderColor: ctx.color,
																	color: ctx.color,
																	backgroundColor: `${ctx.color}1a`,
																}}
																title={ctx.label}
															>
																{r.consensusScore ?? 0}
															</span>
															{r.primaryArea ? `· ${r.primaryArea.name}` : ''}
														</span>
													)
												})()}
											</li>
										))}
									</ul>
								</div>
							</section>
						)}

						{/* Cuisine siblings - SEO internal linking */}
						{cuisineSiblings.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										More {restaurant.cuisine} in London
									</h2>
									<ul className="space-y-2 text-sm">
										{cuisineSiblings.map((r) => (
											<li
												key={r.id}
												className="flex items-center justify-between gap-3"
											>
												<Link
													to={`/london/restaurants/${r.slug}`}
													className="hover:text-[#00e0ff]"
												>
													{r.name}
												</Link>
												{(() => {
													const ctx = getScoreContext(r.consensusScore ?? 0)
													return (
														<span className="flex items-center gap-2 text-[#7070a0]">
															{r.priceRange}
															<span
																className="badge badge-xs font-semibold"
																style={{
																	borderColor: ctx.color,
																	color: ctx.color,
																	backgroundColor: `${ctx.color}1a`,
																}}
																title={ctx.label}
															>
																{r.consensusScore ?? 0}
															</span>
															{r.primaryArea ? `· ${r.primaryArea.name}` : ''}
														</span>
													)
												})()}
											</li>
										))}
									</ul>
								</div>
							</section>
						)}

						{/* Vibe siblings - shared attribute/intent tags */}
						{vibeSiblings.length > 0 && (
							<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
								<div className="card-body space-y-3 text-[#f8f8ff]">
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										Similar vibe
									</h2>
									<ul className="space-y-2 text-sm">
										{vibeSiblings.map((r) => (
											<li
												key={r.id}
												className="flex items-center justify-between gap-3"
											>
												<Link
													to={`/london/restaurants/${r.slug}`}
													className="hover:text-[#00e0ff]"
												>
													{r.name}
												</Link>
												{(() => {
													const ctx = getScoreContext(r.consensusScore ?? 0)
													return (
														<span className="flex items-center gap-2 text-[#7070a0]">
															{r.cuisine} · {r.priceRange}
															<span
																className="badge badge-xs font-semibold"
																style={{
																	borderColor: ctx.color,
																	color: ctx.color,
																	backgroundColor: `${ctx.color}1a`,
																}}
																title={ctx.label}
															>
																{r.consensusScore ?? 0}
															</span>
															{r.primaryArea ? `· ${r.primaryArea.name}` : ''}
														</span>
													)
												})()}
											</li>
										))}
									</ul>
								</div>
							</section>
						)}

						<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15]">
							<div className="card-body space-y-4 text-[#f8f8ff]">
								<div className="space-y-2">
									<p className="text-xs font-semibold tracking-[0.22em] text-[#7de9ff] uppercase">
										Feedback
									</p>
									<h2 className="card-title text-2xl font-black tracking-tight text-[#422ad5]">
										Spot something off?
									</h2>
									<p className="text-sm leading-6 text-[#b0b0e0]">
										If a recommendation feels outdated, the map is wrong, or
										there is something that would make this page more useful,
										send it through.
									</p>
								</div>
								<div className="grid gap-3 sm:grid-cols-2">
									<Link
										to={pageIssueFeedbackHref}
										className="btn w-full border-[#00e0ff] bg-[#00e0ff] text-center text-[#03131a] hover:border-[#7de9ff] hover:bg-[#7de9ff]"
									>
										Report a page issue
									</Link>
									<Link
										to={featureRequestFeedbackHref}
										className="btn btn-outline w-full border-[#7de9ff] text-center text-[#7de9ff] hover:bg-[#7de9ff] hover:text-[#04101d]"
									>
										Suggest a feature
									</Link>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</main>
	)
}
